import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  GraduationCap, BookOpen, Target, Flame, Trophy,
  ChevronRight, Sparkles, Loader2, Calendar, Timer, Zap, X, CalendarPlus, CalendarX, Headphones,
} from "lucide-react";
import { MockExam } from "@/components/certprep/MockExam";
import { MockTrend } from "@/components/certprep/MockTrend";
import { Lectures } from "@/components/certprep/Lectures";

const EXAM_LABEL = {
  cphq: { name: "CPHQ", color: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  cphrm: { name: "CPHRM", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  chc: { name: "CHC", color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};

export default function CertPrep() {
  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="certprep-page">
      <div className="flex items-center gap-3">
        <GraduationCap className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">Cert Prep</h1>
          <p className="text-xs text-muted-foreground">
            CPHQ · CPHRM · CHC — 90-day integrated study system
          </p>
        </div>
      </div>

      <Tabs defaultValue="plan" className="space-y-4">
        <TabsList className="w-full sm:w-auto justify-start overflow-x-auto">
          <TabsTrigger value="plan" data-testid="tab-plan"><span className="hidden sm:inline">Study&nbsp;</span>Plan</TabsTrigger>
          <TabsTrigger value="drill" data-testid="tab-drill">Drill</TabsTrigger>
          <TabsTrigger value="mock" data-testid="tab-mock"><Timer className="w-3.5 h-3.5 mr-1" />Mock</TabsTrigger>
          <TabsTrigger value="listen" data-testid="tab-listen"><Headphones className="w-3.5 h-3.5 mr-1" />Listen</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Stats</TabsTrigger>
        </TabsList>

        <TabsContent value="plan"><PlanTab /></TabsContent>
        <TabsContent value="drill"><DrillTab /></TabsContent>
        <TabsContent value="mock"><MockExam /></TabsContent>
        <TabsContent value="listen"><Lectures /></TabsContent>
        <TabsContent value="stats"><StatsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------- Study Plan tab --------------------------- */
function PlanTab() {
  const [plan, setPlan] = useState(null);
  const [targetDate, setTargetDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/certprep/plan`, { withCredentials: true });
      setPlan(data);
      setTargetDate(data.target_date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load plan");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!targetDate) return;
    setSaving(true);
    try {
      await axios.put(`${API}/certprep/target-date`, { target_date: targetDate }, { withCredentials: true });
      toast.success("Target date saved — grid realigned");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save date");
    } finally { setSaving(false); }
  };

  const [busyWeek, setBusyWeek] = useState(null);
  const scheduleWeek = async (wk) => {
    setBusyWeek(wk.week);
    try {
      const { data } = await axios.post(`${API}/certprep/plan/schedule-week/${wk.week}`, null,
        { params: wk.scheduled_blocks ? { replace: true } : {}, withCredentials: true });
      const n = data.blocks.length;
      if (!n) toast.warning("No free evening slots left this week — try another week");
      else toast.success(`Added ${n} study block${n === 1 ? "" : "s"} (${data.scheduled_hours}h) to your calendar, 6–8 PM${data.shortfall_hours ? ` · ${data.shortfall_hours}h couldn't fit` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't schedule");
    } finally { setBusyWeek(null); }
  };
  const unscheduleWeek = async (wk) => {
    setBusyWeek(wk.week);
    try {
      const { data } = await axios.delete(`${API}/certprep/plan/schedule-week/${wk.week}`, { withCredentials: true });
      toast.success(`Removed ${data.removed} study blocks from your calendar`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    } finally { setBusyWeek(null); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !plan) return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading plan…</div>;

  const phaseColors = {
    Foundation: "bg-slate-500/15 text-slate-300 border-slate-500/40",
    CPHQ: "bg-violet-500/15 text-violet-300 border-violet-500/40",
    CHC: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    CPHRM: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    Mocks: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <Calendar className="w-4 h-4 text-primary" />
          <label className="text-sm font-medium">Exam target date:</label>
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className="h-9 rounded-md border border-zinc-700 bg-transparent px-2 text-sm"
            data-testid="target-date-input"
          />
          <Button size="sm" onClick={save} disabled={saving || !targetDate} data-testid="apply-target-date-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save
          </Button>
          {plan.is_saved ? (
            <Badge
              className={plan.days_until <= 14 ? "bg-rose-500/15 text-rose-300 border-rose-500/40" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"}
              data-testid="target-countdown"
            >
              {plan.days_until >= 0 ? `${plan.days_until} day${plan.days_until === 1 ? "" : "s"} to exam` : `${-plan.days_until} day${plan.days_until === -1 ? "" : "s"} past target`}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="target-unsaved">
              Not saved — pick your real exam date
            </Badge>
          )}
          <div className="ml-auto text-xs text-muted-foreground">
            {plan.total_hours} total study hours · {plan.weeks.length} weeks
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {plan.weeks.map((wk) => (
          <Card
            key={wk.week}
            className={wk.is_current ? "border-primary/60 shadow-md shadow-primary/10" : ""}
            data-testid={`plan-week-${wk.week}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base md:text-lg leading-tight">
                    Week {wk.week} · {wk.title}
                  </CardTitle>
                  <div className="text-xs text-muted-foreground mt-1">
                    {wk.start_date} → {wk.end_date} · {wk.hours}h
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <Badge className={phaseColors[wk.phase]}>{wk.phase}</Badge>
                  {wk.is_current && <Badge variant="outline" className="text-[10px]">NOW</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {wk.exams.map(ex => (
                  <Badge key={ex} variant="outline" className={`text-[10px] ${EXAM_LABEL[ex].color}`}>
                    {EXAM_LABEL[ex].name}
                  </Badge>
                ))}
              </div>
              <ul className="text-sm text-foreground/90 space-y-1 list-disc pl-5">
                {wk.topics.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              {wk.end_date >= new Date().toISOString().slice(0, 10) && (
                <div className="flex items-center gap-2 pt-2 flex-wrap" data-testid={`week-schedule-${wk.week}`}>
                  {wk.scheduled_blocks > 0 && (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40" data-testid={`week-scheduled-badge-${wk.week}`}>
                      <Calendar className="w-3 h-3 mr-1" />{wk.scheduled_blocks} blocks · {wk.scheduled_hours}h on calendar
                    </Badge>
                  )}
                  <Button
                    size="sm" variant={wk.scheduled_blocks ? "ghost" : "outline"} className="h-7 text-xs"
                    disabled={busyWeek === wk.week}
                    onClick={() => scheduleWeek(wk)}
                    data-testid={`schedule-week-btn-${wk.week}`}
                  >
                    {busyWeek === wk.week ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5 mr-1" />}
                    {wk.scheduled_blocks ? "Reschedule" : `Schedule ${wk.hours}h · evenings 6–8 PM`}
                  </Button>
                  {wk.scheduled_blocks > 0 && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-300" disabled={busyWeek === wk.week}
                      onClick={() => unscheduleWeek(wk)} data-testid={`unschedule-week-btn-${wk.week}`}>
                      <CalendarX className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* --------------------------- Drill tab --------------------------- */
function DrillTab() {
  const [exam, setExam] = useState("cphq");
  const [difficulty, setDifficulty] = useState("medium");
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [showRationale, setShowRationale] = useState(false);
  const [daily, setDaily] = useState(null); // {queue: [...exams], done, correct, next: prefetched q}

  const fetchQ = (ex, diff) =>
    axios.post(`${API}/certprep/question`, { exam: ex, difficulty: diff }, { withCredentials: true }).then(r => r.data);

  const generate = async () => {
    setLoading(true);
    setQ(null);
    setChosen(null);
    setShowRationale(false);
    try {
      setQ(await fetchQ(exam, difficulty));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Question generation failed");
    } finally {
      setLoading(false);
    }
  };

  const prefetch = (queue) => {
    if (!queue.length) return;
    fetchQ(queue[0], "medium")
      .then(nq => setDaily(d => d ? { ...d, next: nq } : d))
      .catch(() => {});
  };

  const startDaily = async () => {
    const queue = [...Array(5).fill("cphq"), ...Array(5).fill("chc")];
    setChosen(null); setShowRationale(false); setQ(null); setLoading(true);
    try {
      const first = await fetchQ(queue[0], "medium");
      const rest = queue.slice(1);
      setDaily({ queue: rest, done: 0, correct: 0, total: 10, next: null });
      setQ(first);
      prefetch(rest);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Daily 10");
    } finally { setLoading(false); }
  };

  const dailyNext = async () => {
    if (!daily) return;
    setChosen(null); setShowRationale(false);
    const rest = daily.queue.slice(1);
    let nq = daily.next;
    if (!nq) {
      setLoading(true); setQ(null);
      try { nq = await fetchQ(daily.queue[0], "medium"); }
      catch (e) { toast.error("Question generation failed"); setLoading(false); return; }
      setLoading(false);
    }
    setQ(nq);
    setDaily(d => ({ ...d, queue: rest, next: null }));
    prefetch(rest);
  };

  const submit = async (letter) => {
    if (!q || chosen) return;
    setChosen(letter);
    setShowRationale(true);
    try {
      await axios.post(`${API}/certprep/attempt`, {
        question_id: q.question_id,
        exam: q.exam,
        chosen: letter,
        correct: q.correct,
        topic: q.topic,
      }, { withCredentials: true });
      if (letter === q.correct) toast.success("Correct!");
      if (daily) setDaily(d => ({ ...d, done: d.done + 1, correct: d.correct + (letter === q.correct ? 1 : 0) }));
    } catch (e) {
      console.warn("attempt log failed", e);
    }
  };

  const dailyFinished = daily && daily.done >= daily.total;

  const optBase = "text-left border rounded-lg p-3 transition-all";
  const getOptClass = (letter) => {
    if (!chosen) return `${optBase} border-zinc-700 hover:border-primary hover:bg-primary/5 cursor-pointer`;
    if (letter === q.correct) return `${optBase} border-emerald-500 bg-emerald-500/10`;
    if (letter === chosen) return `${optBase} border-rose-500 bg-rose-500/10`;
    return `${optBase} border-zinc-800 opacity-60`;
  };

  return (
    <div className="space-y-4">
      {/* Daily 10 banner */}
      {daily && !dailyFinished && (
        <Card className="border-primary/50" data-testid="daily10-banner">
          <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold">Daily 10</span>
            <span className="text-xs text-muted-foreground">5 CPHQ · 5 CHC · medium</span>
            <span className="ml-auto text-sm tabular-nums" data-testid="daily10-progress">
              {daily.done}/{daily.total} · <span className="text-emerald-300">{daily.correct} correct</span>
            </span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDaily(null)} data-testid="daily10-exit-btn"><X className="w-3.5 h-3.5" /></Button>
          </CardContent>
          <Progress value={(daily.done / daily.total) * 100} className="h-1 rounded-none" />
        </Card>
      )}

      {dailyFinished && (
        <Card className="border-emerald-500/50" data-testid="daily10-done">
          <CardContent className="pt-6 pb-6 text-center space-y-2">
            <Flame className="w-8 h-8 mx-auto text-orange-400" />
            <div className="text-xl font-bold">Daily 10 complete — streak seeded</div>
            <div className="text-sm text-muted-foreground">{daily.correct}/{daily.total} correct ({Math.round(daily.correct * 10)}%)</div>
            <div className="flex justify-center gap-2 pt-2">
              <Button size="sm" onClick={startDaily} data-testid="daily10-again-btn"><Zap className="w-3.5 h-3.5 mr-1" /> Run another 10</Button>
              <Button size="sm" variant="outline" onClick={() => { setDaily(null); setQ(null); }} data-testid="daily10-close-btn">Back to drill</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      {!daily && (
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {["cphq", "cphrm", "chc"].map(ex => (
              <Button
                key={ex}
                size="sm"
                variant={exam === ex ? "default" : "outline"}
                onClick={() => setExam(ex)}
                data-testid={`drill-exam-${ex}`}
              >
                {EXAM_LABEL[ex].name}
              </Button>
            ))}
          </div>
          <div className="flex gap-1">
            {["easy", "medium", "hard"].map(d => (
              <Button
                key={d}
                size="sm"
                variant={difficulty === d ? "default" : "outline"}
                onClick={() => setDifficulty(d)}
                className="capitalize"
              >
                {d}
              </Button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={startDaily} disabled={loading} data-testid="daily10-start-btn">
              <Zap className="w-4 h-4 mr-2 text-amber-400" /> Daily 10
            </Button>
            <Button
              onClick={generate}
              disabled={loading}
              data-testid="generate-question-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {q ? "Next question" : "Generate question"}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {daily && !dailyFinished && chosen && (
        <Button onClick={dailyNext} disabled={loading} className="w-full" data-testid="daily10-next-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
          Next ({EXAM_LABEL[daily.queue[0]]?.name}) — {daily.total - daily.done} left
        </Button>
      )}

      {loading && !q && (
        <div className="p-6 flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing question…</div>
      )}

      {/* Question */}
      {q && !dailyFinished && (
        <Card data-testid="drill-question">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Badge className={EXAM_LABEL[q.exam].color}>{EXAM_LABEL[q.exam].name}</Badge>
                <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>
              </div>
            </div>
            <CardTitle className="text-base md:text-lg leading-relaxed mt-3">
              {q.stem}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {["A", "B", "C", "D"].map(letter => (
              <button
                key={letter}
                onClick={() => submit(letter)}
                disabled={!!chosen}
                className={`w-full ${getOptClass(letter)}`}
                data-testid={`drill-option-${letter}`}
              >
                <span className="font-bold text-primary mr-2">{letter}.</span>
                <span>{q.options[letter]}</span>
              </button>
            ))}

            {showRationale && (
              <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/30 space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  <span className="font-semibold">Correct answer: {q.correct}</span>
                  {chosen === q.correct
                    ? <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">You got it</Badge>
                    : <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40">You picked {chosen}</Badge>}
                </div>
                <p className="text-sm leading-relaxed">{q.rationale}</p>
                {Object.entries(q.distractor_notes || {}).some(([, v]) => v) && (
                  <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-zinc-800">
                    <div className="font-semibold text-foreground/80">Why the others miss:</div>
                    {["A", "B", "C", "D"].map(l => {
                      if (l === q.correct) return null;
                      const note = q.distractor_notes?.[l];
                      if (!note) return null;
                      return <div key={l}><span className="font-bold">{l}:</span> {note}</div>;
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!q && !loading && !daily && (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <div className="text-sm">Pick an exam + difficulty, then tap <b>Generate question</b> — or hit <b>Daily 10</b> to seed your streak.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* --------------------------- Stats tab --------------------------- */
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          axios.get(`${API}/certprep/stats`, { withCredentials: true }),
          axios.get(`${API}/certprep/history`, { params: { limit: 15 }, withCredentials: true }),
        ]);
        setStats(s.data);
        setHistory(h.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load stats");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading stats…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-6 text-center">
            <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-400" />
            <div className="text-2xl font-bold">{stats.total_attempts}</div>
            <div className="text-xs text-muted-foreground">Total attempts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <Flame className="w-5 h-5 mx-auto mb-1 text-orange-400" />
            <div className="text-2xl font-bold">{stats.streak_days}</div>
            <div className="text-xs text-muted-foreground">Day streak</div>
          </CardContent>
        </Card>
        {Object.entries(stats.by_exam).slice(0, 2).map(([ex, s]) => (
          <Card key={ex}>
            <CardContent className="pt-6 text-center">
              <Badge className={`mb-1 ${EXAM_LABEL[ex].color}`}>{EXAM_LABEL[ex].name}</Badge>
              <div className="text-2xl font-bold">{s.accuracy}%</div>
              <div className="text-xs text-muted-foreground">{s.correct}/{s.total} correct</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <MockTrend />

      <Card>
        <CardHeader><CardTitle className="text-base">Accuracy by exam</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {["cphq", "cphrm", "chc"].map(ex => {
            const s = stats.by_exam[ex];
            return (
              <div key={ex}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{EXAM_LABEL[ex].name}</span>
                  <span className="text-muted-foreground">{s.correct}/{s.total} · {s.accuracy}%</span>
                </div>
                <Progress value={s.accuracy} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent attempts</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 && (
            <div className="text-sm text-muted-foreground">No attempts yet. Head to the Drill tab.</div>
          )}
          {history.map((a) => (
            <div key={a.attempt_id} className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0">
              <Badge className={`text-[10px] ${EXAM_LABEL[a.exam]?.color || ""}`}>
                {EXAM_LABEL[a.exam]?.name || a.exam}
              </Badge>
              <div className="text-sm truncate flex-1">{a.topic || "—"}</div>
              <Badge className={a.is_correct
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : "bg-rose-500/20 text-rose-300 border-rose-500/40"}>
                {a.is_correct ? "✓" : "✗"} {a.chosen}
              </Badge>
              <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
